import { useCallback, useRef, useState } from 'react';
import { useClearFocusedAsset } from '@frameio/selection';
import { AlertDialog, AlertDialogButton } from '@frameio/vapor';

type WarningState = 'none' | 'restricted' | 'cross-project' | 'completed';

export type AssetOperationArgs = {
  triggeredByRestricted?: boolean;
  triggeredByCrossProject?: boolean;
  force?: boolean;
  [key: string]: any;
};

type WarningDialogProps = {
  isShowing: boolean;
  title: string;
  details: string;
  confirmButtonText: string;
};

type UseAssetWarningFlowProps<T extends AssetOperationArgs> = {
  executeOperation: (args: T & { force: true }) => void;
  cancelText: string;
};

export function useAssetWarningFlow<T extends AssetOperationArgs>({
  executeOperation,
  cancelText,
}: UseAssetWarningFlowProps<T>) {
  const [warningState, setWarningState] = useState<WarningState>('none');
  const operationArgs = useRef<T | null>(null);
  const leastDestructiveRef = useRef(null);
  const clearFocusedAsset = useClearFocusedAsset();

  const handleWarningConfirm = useCallback(() => {
    if (!operationArgs.current) return;

    const stateTransitions: Partial<Record<WarningState, () => void>> = {
      restricted: () => {
        if (operationArgs.current?.triggeredByCrossProject) {
          setWarningState('cross-project');
        } else {
          processOperation();
        }
      },
      'cross-project': () => {
        processOperation();
      },
    };

    const processOperation = () => {
      executeOperation({
        ...operationArgs.current!,
        force: true,
      });
      setWarningState('none');
    };

    stateTransitions[warningState]?.();
  }, [executeOperation, warningState]);

  const handleWarningCancel = useCallback(() => {
    setWarningState('none');
    clearFocusedAsset();
  }, [clearFocusedAsset]);

  const createWarningDialog = useCallback(
    (props: WarningDialogProps) => {
      const { isShowing, title, details, confirmButtonText } = props;

      return (
        <AlertDialog
          actions={
            <>
              <AlertDialogButton
                ref={leastDestructiveRef}
                onClick={handleWarningCancel}
              >
                {cancelText}
              </AlertDialogButton>
              <AlertDialogButton color="primary" onClick={handleWarningConfirm}>
                {confirmButtonText}
              </AlertDialogButton>
            </>
          }
          details={details}
          isShowing={isShowing}
          leastDestructiveRef={leastDestructiveRef}
          onExited={() => {
            if (warningState === 'none') {
              operationArgs.current = null;
            }
          }}
          title={title}
          variant="notice"
        />
      );
    },
    [handleWarningCancel, handleWarningConfirm, warningState, cancelText]
  );

  const startWarningFlow = useCallback((args: T) => {
    operationArgs.current = args;

    if (args.triggeredByRestricted) {
      setWarningState('restricted');
    } else if (args.triggeredByCrossProject) {
      setWarningState('cross-project');
    }
  }, []);

  return {
    warningState,
    startWarningFlow,
    createWarningDialog,
    operationArgs,
  };
}
